import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  CRM_CAMPAIGN_CHANNELS,
  CRM_CAMPAIGN_STATUSES,
  CRM_DELIVERY_CHANNELS,
  CRM_DELIVERY_STATUSES,
  CRM_SKIP_REASONS,
  leadFilterSchema,
  leadSchema,
  pageRequestSchema,
  pageResponseSchema,
} from './crm.contract'

/**
 * CRM campaigns (PD-1993, docs/CRM.md): an SMS, an email, or both, sent to a cohort of leads. A
 * draft's name, message and cohort can change; launching freezes the cohort and sends to it
 * through our own SNS (SMS) and SES (email). Each message — one per member per channel — is
 * recorded as a delivery. Same access as the leads.
 */

/** `{{firstName}}` in a message is replaced with the lead's first name ("there" when unknown). */
export const CRM_FIRST_NAME_TOKEN = '{{firstName}}'

/** Every SMS ends with this, so a lead can always opt out (SNS honours STOP). */
export const CRM_SMS_OPT_OUT = 'Reply STOP to opt out.'

/** The most leads one add puts in a cohort; adding again takes the next of those not in it. */
export const CRM_COHORT_ADD_LIMIT = 5000

/** The longest text one SNS publish sends, in characters — the opt-out line included. */
export const CRM_SMS_MAX_LENGTH = 1600

/** A longer first name reads "there" in a text, so a message's longest text is known on save. */
export const CRM_SMS_NAME_MAX = 30

/** The longest text a message can make: every first name at CRM_SMS_NAME_MAX, plus the opt-out. */
export function crmSmsMaxLength(message: string): number {
  const text = message.split(CRM_FIRST_NAME_TOKEN).join('x'.repeat(CRM_SMS_NAME_MAX))
  return `${text}\n\n${CRM_SMS_OPT_OUT}`.length
}

/** `members` counts leads; the statuses count deliveries (messages: two per lead for `both`). */
const deliveryCountsSchema = z.object({
  members: z.number().int().min(0),
  pending: z.number().int().min(0),
  sending: z.number().int().min(0),
  sent: z.number().int().min(0),
  failed: z.number().int().min(0),
  skipped: z.number().int().min(0),
})

export const campaignSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  channel: z.enum(CRM_CAMPAIGN_CHANNELS),
  subject: z.string().nullable(),
  message: z.string(),
  status: z.enum(CRM_CAMPAIGN_STATUSES),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** When the recipients last changed; a launch carries the one it reviewed. */
  cohortUpdatedAt: z.string(),
  launchedByName: z.string().nullable(),
  launchedAt: z.string().nullable(),
  counts: deliveryCountsSchema,
})

const campaignFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200),
  channel: z.enum(CRM_CAMPAIGN_CHANNELS),
  /** Required to launch when email is included (`email`, `both`); ignored for SMS only. */
  subject: z.string().trim().max(200).nullable().optional(),
  message: z.string().trim().min(1).max(10_000),
})

/** A campaign that texts must fit its longest text in one SNS publish. */
const smsFits = (
  fields: { channel: (typeof CRM_CAMPAIGN_CHANNELS)[number]; message: string },
  ctx: z.RefinementCtx,
) => {
  if (fields.channel !== 'email' && crmSmsMaxLength(fields.message) > CRM_SMS_MAX_LENGTH) {
    ctx.addIssue({
      code: 'custom',
      path: ['message'],
      message: `A text can be up to ${CRM_SMS_MAX_LENGTH.toLocaleString('en-US')} characters, opt-out line included`,
    })
  }
}

const campaignIdSchema = z.object({ campaignId: z.number().int() })

export const campaignCreateInputSchema = campaignFieldsSchema.superRefine(smsFits)
export const campaignUpdateInputSchema = campaignIdSchema
  .extend({
    /** The draft's updatedAt as the editor read it; a save against an older one is CONFLICT. */
    updatedAt: z.string(),
  })
  .extend(campaignFieldsSchema.shape)
  .superRefine(smsFits)

export const crmCampaignList = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/list' })
  .input(
    z.object({
      page: pageRequestSchema,
      /** Matches the campaign's name. */
      search: z.string().trim().max(200).optional(),
    }),
  )
  .output(z.object({ page: pageResponseSchema, data: z.array(campaignSchema) }))

export const crmCampaignGet = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/get' })
  .input(campaignIdSchema)
  .output(campaignSchema)

export const crmCampaignCreate = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/create' })
  .input(campaignCreateInputSchema)
  .output(campaignSchema)

/** A draft only, as of the version read; a launched campaign, or a newer save, is CONFLICT. */
export const crmCampaignUpdate = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/update' })
  .input(campaignUpdateInputSchema)
  .output(campaignSchema)

/**
 * Freezes the cohort, creates one delivery per member per channel, and starts sending in the
 * background. Not-interested leads are skipped on every channel, emails to leads with no email
 * and texts to leads with no textable phone are skipped. Calling it again on a launched campaign
 * sends whatever is still pending (after a restart); a message caught mid-send is never resent.
 */
export const crmCampaignLaunch = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/launch' })
  .input(
    campaignIdSchema.extend({
      /** The draft's updatedAt as the coordinator reviewed it; a newer save is CONFLICT. */
      updatedAt: z.string().optional(),
      /** Its cohortUpdatedAt as reviewed; recipients changed since are CONFLICT. */
      cohortUpdatedAt: z.string().optional(),
    }),
  )
  .output(campaignSchema)

/** One message to a member on one channel. */
export const deliverySchema = z.object({
  channel: z.enum(CRM_DELIVERY_CHANNELS),
  status: z.enum(CRM_DELIVERY_STATUSES),
  skipReason: z.enum(CRM_SKIP_REASONS).nullable(),
  deliveredAt: z.string().nullable(),
})

export const cohortMemberSchema = z.object({
  lead: leadSchema,
  addedAt: z.string(),
  /** Empty while the campaign is a draft; one per channel once launched, SMS first. */
  deliveries: z.array(deliverySchema),
})

export const crmCohortList = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/cohort/list' })
  .input(campaignIdSchema.extend({ page: pageRequestSchema }))
  .output(z.object({ page: pageResponseSchema, data: z.array(cohortMemberSchema) }))

/**
 * Adds leads to a draft's cohort: the listed ids, or every lead matching the filter (the CRM
 * list's own filters). Leads already in the cohort are left as they are.
 */
export const crmCohortAdd = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/cohort/add' })
  .input(
    campaignIdSchema.extend({
      leadIds: z.array(z.number().int()).max(1000).optional(),
      filter: leadFilterSchema.optional(),
    }),
  )
  .output(z.object({ added: z.number().int().min(0), campaign: campaignSchema }))

/** One campaign a lead is in, with what happened to their message. */
export const leadCampaignSchema = z.object({
  campaignId: z.number().int(),
  name: z.string(),
  channel: z.enum(CRM_CAMPAIGN_CHANNELS),
  status: z.enum(CRM_CAMPAIGN_STATUSES),
  addedAt: z.string(),
  launchedAt: z.string().nullable(),
  /** Empty while the campaign is a draft; one per channel once launched, SMS first. */
  deliveries: z.array(deliverySchema),
})

/** The campaigns a lead of the caller's project is in, most recently added first. */
export const crmLeadCampaigns = oc
  .route({ method: 'POST', path: '/v2/crm/leads/campaigns' })
  .input(z.object({ leadId: z.number().int(), page: pageRequestSchema }))
  .output(z.object({ page: pageResponseSchema, data: z.array(leadCampaignSchema) }))

/** Removes leads from a draft's cohort. */
export const crmCohortRemove = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/cohort/remove' })
  .input(campaignIdSchema.extend({ leadIds: z.array(z.number().int()).min(1).max(1000) }))
  .output(z.object({ removed: z.number().int().min(0), campaign: campaignSchema }))

/** Removes every lead from a draft's cohort. */
export const crmCohortClear = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/cohort/clear' })
  .input(campaignIdSchema)
  .output(z.object({ removed: z.number().int().min(0), campaign: campaignSchema }))
